/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 ***/

import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Import API actions for approving or denying vehicle documents
import {
    ApproveOrDeniedVehicleContratorDocs,
    ApproveOrDeniedVehicleSupplierDocs
} from "../../../../../reduxToolkit/ApproveAndDeniedVehiclesDocs/ApproveAndDeniedVehicleDocsApi";

import { toast } from "react-toastify";

// Enum for user types
import { USER_TYPE_MODEL } from "../../../../../enums/userTypeEnum";

// Import API actions for different user types
import { ApproveCompanyEmployeeDocument } from "../../../../../reduxToolkit/Employee/EmployeeApi";
import { ApproveContractorDocument } from "../../../../../reduxToolkit/EmployeeContractors/EmployeeContractorsApi";
import { ApproveExternalDocument } from "../../../../../reduxToolkit/EmployeeProviders/EmployeeProvidersApi";
import {MODELS_NAME} from "../../../../../Apis/Models";
import {errorCodeTranslation} from "../../../../../Apis/ErrorCode";
import {errorMsg} from "../../../../../Apis/functions";

/**
 * Custom hook to handle document approval logic based on document type and user role.
 *
 * @param {string} documentId - The ID of the document to approve.
 * @param {string} modelVehicle - Indicates if the document is vehicle-related.
 * @param {string} userType - The type of user (employee, contractor, etc.).
 * @param {function} setUpdated - Function to trigger a state update after success.
 * @param {function} onHide - Function to hide the modal or close the form.
 */
const ApproveDocumentApp = ({ documentId, modelVehicle, userType, setUpdated, onHide }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const handle = () => {
        const data = {
            comments: '',
            id: documentId,
            validated: true,
        };

        if (modelVehicle === MODELS_NAME.contractorVehicle) {
            dispatch(ApproveOrDeniedVehicleContratorDocs(data)).then(
                ({
                     payload: {
                         data: { success , code},
                     },
                 }) => {
                    if (success) successMsg();
                    else errorMsg(code);
                },
            );
        } else if (modelVehicle === MODELS_NAME.supplierVehicle) {
            dispatch(ApproveOrDeniedVehicleSupplierDocs(data)).then(
                ({
                     payload: {
                         data: { success,code },
                     },
                 }) => {
                    if (success) successMsg();
                    else errorMsg(code);
                },
            );
        }
        else {
            switch (userType) {
                case USER_TYPE_MODEL.EMPLOYEE:
                    dispatch(ApproveCompanyEmployeeDocument(data)).then(
                        ({
                             payload: {
                                 data: {success,code},
                             },
                         }) => {
                            if (success) successMsg();
                            else errorMsg(code);
                        },
                    );
                    break;
                case USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE:
                case USER_TYPE_MODEL.CONTRACTORS_EMPLOYEE:
                    dispatch(ApproveContractorDocument(data)).then(
                        ({
                             payload: {
                                 data: {success,code},
                             },
                         }) => {
                            if (success) successMsg();
                            else errorMsg(code);
                        },
                    );
                    break;
                case USER_TYPE_MODEL.SUPPLIER_IN_CHARGE:
                case USER_TYPE_MODEL.SUPPLIER_EMPLOYEE:
                    dispatch(ApproveExternalDocument(data)).then(
                        ({
                             payload: {
                                 data: {success,code},
                             },
                         }) => {
                            if (success) successMsg();
                            else errorMsg(code);
                        },
                    );
                    break;
                default:
                    break;
            }
        }
    }

    // Function to show a success toast and trigger UI updates
    const successMsg = () => {
        toast.success(t('approved_successfully').toUpperCase());
        setUpdated(true); // Flag to trigger refresh in the parent
        onHide(); // Close modal/dialog
    }


    return {
        t, handle
    };
};

export default ApproveDocumentApp;
